import { useState } from "react";
import { Contact } from "../lib/api";

interface Props {
  contact?: Contact | null;            // null/undefined → create a new contact
  onClose: () => void;
  onSave: (patch: Partial<Contact>) => void;
}

export default function EditContactModal({ contact, onClose, onSave }: Props) {
  const isNew = !contact;
  const [name,      setName]      = useState(contact?.name || "");
  const [title,     setTitle]     = useState(contact?.title || "");
  const [company,   setCompany]   = useState(contact?.company || "");
  const [country,   setCountry]   = useState(contact?.country || "");
  const [type,      setType]      = useState(contact?.type || "");
  const [email,     setEmail]     = useState(contact?.email || "");
  const [altEmails, setAltEmails] = useState(parseAlts(contact?.alt_emails));
  const [phone,     setPhone]     = useState(contact?.phone || "");
  const [notes,     setNotes]     = useState(contact?.notes || "");

  function submit() {
    if (isNew && !name.trim()) { alert("Name is required."); return; }
    const cleanAlts = altEmails.split(/[\n,;]+/).map(s => s.trim()).filter(Boolean);
    onSave({
      name:    name.trim(),
      title:   title.trim() || null,
      company: company.trim() || null,
      country: country.trim() || null,
      type:    type.trim() || null,
      email:   email.trim() || null,
      alt_emails: cleanAlts.length ? JSON.stringify(cleanAlts) : null,
      phone:   phone.trim() || null,
      notes:   notes.trim() || null,
    });
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
      onMouseDown={(e) => { if (e.button === 0 && e.target === e.currentTarget) onClose(); }}
    >
      <div className="card w-full max-w-2xl p-5 shadow-xl">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h2 className="text-base font-semibold tracking-tightish text-slate-900">{isNew ? "Add contact" : `Edit contact #${contact.id}`}</h2>
            <p className="mt-0.5 text-xs text-slate-500">{isNew ? "Name is required. Other fields are optional." : "Update any field. Blank fields clear the value."}</p>
          </div>
          <button className="btn-ghost !text-xs !py-1 !px-2" onClick={onClose}>Close</button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Name"    value={name}    onChange={setName} />
          <Field label="Title"   value={title}   onChange={setTitle} />
          <Field label="Company" value={company} onChange={setCompany} />
          <Field label="Country" value={country} onChange={setCountry} />
          <Field label="Type"    value={type}    onChange={setType} />
          <Field label="Phone"   value={phone}   onChange={setPhone} />
          <Field label="Primary email" value={email} onChange={setEmail} placeholder="name@company.com" colSpan={2} />
          <div className="col-span-2">
            <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-slate-500">Alternate emails</label>
            <textarea
              className="w-full !text-xs !py-1.5 !px-2.5"
              rows={2}
              value={altEmails}
              placeholder="one per line, or comma-separated"
              onChange={(e) => setAltEmails(e.target.value)}
            />
          </div>
          <div className="col-span-2">
            <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-slate-500">Notes</label>
            <textarea
              className="w-full !text-xs !py-1.5 !px-2.5"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <button className="btn-secondary !text-xs !py-1.5 !px-3" onClick={onClose}>Cancel</button>
          <button className="btn-primary !text-xs !py-1.5 !px-3" onClick={submit}>{isNew ? "Add contact" : "Save changes"}</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, colSpan }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string; colSpan?: number }) {
  return (
    <div className={colSpan === 2 ? "col-span-2" : undefined}>
      <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</label>
      <input type="text" className="w-full !text-xs !py-1.5 !px-2.5" value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function parseAlts(raw: string | null | undefined): string {
  if (!raw) return "";
  try {
    const p = JSON.parse(raw);
    if (Array.isArray(p)) return p.join("\n");
  } catch { /* fall through */ }
  return String(raw);
}
